import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

export interface TranslateStreamParams {
  text: string;
  to: string;
  from_lang: string;
}

/**
 * Custom hook for SSE streaming translation
 * Wraps complex streaming logic while maintaining react-query error patterns
 */
export function useTranslateStreamMutation(
  onChunk?: (chunk: string) => void,
  options?: UseMutationOptions<void, Error, TranslateStreamParams>,
) {
  return useMutation({
    mutationFn: async (params: TranslateStreamParams) => {
      const controller = new AbortController();
      
      try {
        const res = await fetch('/api/translate-stream', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
          signal: controller.signal,
        });

        if (!res.ok) {
          const err = await res.text();
          throw new Error(`Translation failed (${res.status}): ${err}`);
        }

        // Read the SSE-like stream from the response body
        const reader = res.body?.getReader();
        if (!reader) {
          throw new Error('No stream available from server');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let hasError = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE messages arrive as lines like: data: <payload>\n\n
          let splitIndex;
          while ((splitIndex = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, splitIndex);
            buffer = buffer.slice(splitIndex + 2);

            // lines might contain multiple prefixed lines; take any 'data: ' lines
            // Do NOT trim here: trimming would remove meaningful newlines from payloads.
            const lines = raw.split('\n');
            for (const line of lines) {
              if (line.startsWith('data:')) {
                // preserve whitespace/newlines in payload; only remove a single
                // leading space if the backend included 'data: <payload>' with a space.
                let payload = line.slice(5);
                if (payload.startsWith(' ')) payload = payload.slice(1);
                // Try to parse JSON-encoded payload (backend sends JSON.dumps(payload))
                try {
                  const parsed = JSON.parse(payload);
                  payload = typeof parsed === 'string' ? parsed : String(parsed);
                } catch (e) {
                  // leave payload as-is
                }
                // Call the onChunk callback with the payload
                onChunk?.(payload);
              } else if (line.startsWith('event:')) {
                // Check if it's an error event
                const eventType = line.slice(6).trim();
                if (eventType === 'error') {
                  hasError = true;
                }
              }
            }
          }
        }

        // flush remaining buffer
        if (buffer.trim()) {
          const lines = buffer.split('\n');
          for (const line of lines) {
            if (line.startsWith('data:')) {
              let payload = line.slice(5);
              if (payload.startsWith(' ')) payload = payload.slice(1);
              try {
                const parsed = JSON.parse(payload);
                payload = typeof parsed === 'string' ? parsed : String(parsed);
              } catch (e) {
                // ignore parse error
              }
              onChunk?.(payload);
            }
          }
        }

        if (hasError) {
          throw new Error('Translation service encountered an error');
        }
      } catch (err: any) {
        if (err.name === 'AbortError') {
          // Silently handle abort (user cancelled)
          return;
        }
        throw err;
      }
    },
    ...options,
  });
}
